import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Clock } from "lucide-react";

const DAY_LABELS = [
  { v: 0, l: "Lun" },
  { v: 1, l: "Mar" },
  { v: 2, l: "Mié" },
  { v: 3, l: "Jue" },
  { v: 4, l: "Vie" },
  { v: 5, l: "Sáb" },
  { v: 6, l: "Dom" },
];

const empty = {
  name: "",
  start_time: "06:00",
  end_time: "11:00",
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  active: true,
};

export default function AdminShifts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/shifts").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(empty);
    setEditing(null);
    setOpen(true);
  };
  const startEdit = (s) => {
    setForm({ ...empty, ...s, days_of_week: s.days_of_week || [0, 1, 2, 3, 4, 5, 6] });
    setEditing(s.id);
    setOpen(true);
  };

  const toggleDay = (d) => {
    const days = form.days_of_week || [];
    setForm({
      ...form,
      days_of_week: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort(),
    });
  };

  const save = async () => {
    try {
      const body = { ...form };
      if (editing) await api.put(`/admin/shifts/${editing}`, body);
      else await api.post("/admin/shifts", body);
      toast.success("Turno guardado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar turno?")) return;
    try {
      await api.delete(`/admin/shifts/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const daysLabel = (days) =>
    (days || [])
      .map((d) => DAY_LABELS.find((x) => x.v === d)?.l)
      .filter(Boolean)
      .join(", ");

  return (
    <div data-testid="admin-shifts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Operación</div>
          <h1 className="font-display text-4xl text-white mt-1">Turnos</h1>
          <p className="text-white/60 text-sm mt-1">Horarios para validar asistencia de suscriptores.</p>
        </div>
        <button onClick={startNew} className="btn-primary text-sm">
          <Plus size={14} /> Nuevo turno
        </button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="glass p-5 rounded-sm">
            <div className="flex justify-between items-start">
              <div className="font-display text-2xl text-white">{s.name}</div>
              <span className={`badge-status ${s.active ? "badge-valid" : "badge-overdue"}`}>
                {s.active ? "Activo" : "Inactivo"}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-white/60 text-sm">
              <Clock size={14} className="text-[#E63946]" />
              {s.start_time} – {s.end_time}
            </div>
            <div className="text-xs text-white/40 mt-2">{daysLabel(s.days_of_week)}</div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => startEdit(s)} className="btn-outline text-xs py-1.5">
                <Pencil size={12} /> Editar
              </button>
              <button onClick={() => del(s.id)} className="text-red-400 ml-auto">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="md:col-span-3 glass p-10 text-center text-white/40">No hay turnos configurados</div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="glass-strong max-w-lg w-full rounded-sm my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-theme-secondary/10">
              <div className="font-display text-2xl text-white">{editing ? "Editar turno" : "Nuevo turno"}</div>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white">
                <X />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60">Nombre</label>
                <input className="input-dark mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Inicio</label>
                  <input type="time" className="input-dark mt-1" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Fin</label>
                  <input type="time" className="input-dark mt-1" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60">Días</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DAY_LABELS.map((d) => (
                    <button
                      key={d.v}
                      type="button"
                      onClick={() => toggleDay(d.v)}
                      className={`px-2 py-1 text-xs rounded-sm border ${
                        (form.days_of_week || []).includes(d.v)
                          ? "border-[#E63946] bg-[#E63946]/20 text-white"
                          : "border-white/20 text-white/60"
                      }`}
                    >
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/60">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Activo
              </label>
            </div>
            <div className="p-5 border-t border-theme-secondary/10 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-outline text-sm py-2">
                Cancelar
              </button>
              <button onClick={save} className="btn-primary text-sm py-2">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
